import React from 'react';
import type { RescueUnassignedInventoryDeviceItemGql } from '../../lib/rescueQueries';
import UnassignedDeviceRow from './UnassignedDeviceRow';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Button from '../../components/Button/Button';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead, { type TableColumn } from './TableHead';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import styles from './Devices.module.scss';

// Device takes everything the Action column leaves, so Action lines up with the Activated
// table above it — the two stack on the Devices tab.
export const STOCK_COLUMNS: TableColumn[] = [
  { label: 'Device', width: `${100 - ACTION_COLUMN_WIDTH}%` },
  { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%` },
];

interface StockTableProps {
  /** Devices for the current page (already sliced by the caller's client pagination). */
  devices: RescueUnassignedInventoryDeviceItemGql[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  hasError: boolean;
  onRetry: () => void;
  onAssign: (moduleId: string) => void;
  onMarkLost?: (moduleId: string) => void;
  onMarkInoperable?: (moduleId: string) => void;
  searchQuery: string;
}

/** Delivered devices not yet attached to a pet (formerly the "Unassigned" group). */
export default function StockTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  loading,
  hasError,
  onRetry,
  onAssign,
  onMarkLost,
  onMarkInoperable,
  searchQuery,
}: StockTableProps) {
  const colCount = STOCK_COLUMNS.length;
  const hasData = devices.length > 0;
  const showSkeleton = loading && totalCount === 0 && !hasError;

  return (
    <CollapsibleSection
      title="Stock"
      count={totalCount}
      description="Devices delivered to your rescue and not yet attached to a pet."
    >
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <TableHead columns={STOCK_COLUMNS} />
          <tbody>
            {/*
              Only when there is nothing else to show. A failed refetch raises the error while
              the last good page is still in hand, and every mutation on the page refetches
              active queries — so taking the rows down on `hasError` alone would drop the user
              out of a section they were working in over one bad response.
            */}
            {hasError && !hasData ? (
              <tr>
                <td colSpan={colCount} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load inventory.</span>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                      Retry
                    </Button>
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={5} columns={colCount} />
            ) : totalCount === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No stock devices matching "${searchQuery}"` : 'No devices in stock'}
                </td>
              </tr>
            ) : (
              devices.map((d) => (
                <UnassignedDeviceRow
                  key={d.moduleId}
                  device={d}
                  onAssign={onAssign}
                  onMarkLost={onMarkLost}
                  onMarkInoperable={onMarkInoperable}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
